import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Bell } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { getPrivacy, savePrivacy } from "@/lib/api";

const VIS = [
  ["phone", "Phone number", "Shop page par Call aur WhatsApp buttons. Hide karne par dono buttons hat jaate hain."],
  ["address", "Public address", "Shop page aur search results me address aur landmark."],
  ["map", "Map location", "Map, directions aur route links. Address se alag chalti hai."],
  ["pincode", "PIN code", "PIN number nahi dikhega, lekin shop PIN search me milti rahegi."],
  ["photo", "Profile photo", "Public profile par aapki photo. Shop logo aur photos par asar nahi."],
];
const NOTIF = [
  ["ad_offers", "Mere PIN ke ad offers", "Bell me aas-paas ki dukanon ke sponsored offers."],
  ["jobs", "Job aur activity alerts", "Aapki vacancy ki last date paas aane ki alert."],
  ["location", "Location share alerts", "Kisi ne location share ki to bell alert. Sharing khud chalti rahegi; apni share khatam hone ka reminder hamesha aayega."],
];

function PrefRow({ id, label, hint, on, busy, onToggle, onText, offText }) {
  return (
    <div className="py-3 flex items-start gap-3" data-testid={`pref-row-${id}`}>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-900">{label}</div>
        <div className="text-xs text-slate-500 mt-0.5">{hint}</div>
      </div>
      <span className={`text-[11px] font-bold mt-0.5 whitespace-nowrap ${on ? "text-emerald-700" : "text-slate-500"}`} data-testid={`pref-state-${id}`}>{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : on ? onText : offText}</span>
      <Switch checked={on} disabled={busy} onCheckedChange={onToggle} aria-label={label} data-testid={`pref-toggle-${id}`} />
    </div>
  );
}

export default function PrivacySettings({ embedded = false }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(false);
  const { hash } = useLocation();
  useEffect(() => { getPrivacy().then(setState).catch(() => setErr(true)); }, []);
  useEffect(() => {
    if (state && hash === "#privacy") setTimeout(() => document.getElementById("privacy")?.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
  }, [state, hash]);

  const toggle = async (group, key, value, label) => {
    setBusy(`${group}.${key}`);
    try {
      setState(await savePrivacy({ [group]: { [key]: value } }));
      toast.success(`${label}: ${value ? (group === "privacy" ? "Shown" : "On") : (group === "privacy" ? "Hidden" : "Off")} — saved`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save nahi ho paya, dobara try karein");
    } finally { setBusy(null); }
  };

  const group = (g, rows, onText, offText) => rows.map(([k, label, hint]) => (
    <PrefRow key={k} id={`${g}-${k}`} label={label} hint={hint} on={state[g][k]} busy={busy === `${g}.${k}`}
      onToggle={(v) => toggle(g, k, v, label)} onText={onText} offText={offText} />
  ));

  return (
    <section id={embedded ? undefined : "privacy"} className={embedded ? "px-2 pb-1" : "scroll-mt-24 bg-white border border-slate-100 rounded-2xl p-5 sm:p-6 mt-8"} data-testid={embedded ? "tb-privacy-settings" : "privacy-visibility-section"}>
      {!embedded && <h2 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-emerald-700" /> Privacy & Notification Settings</h2>}
      <p className="text-xs text-slate-500 mt-1">Har setting alag se save hoti hai. Login, verification aur aapke private account data par asar nahi padta.</p>
      {err && <div className="text-sm text-rose-600 mt-3" data-testid="privacy-load-error">Settings load nahi ho payin. Page refresh karein.</div>}
      {!state && !err && <div className="text-sm text-slate-500 mt-3 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading...</div>}
      {state && (
        <>
          <div className="mt-4 text-sm font-bold text-slate-900 flex items-center gap-2" data-testid="privacy-settings-heading"><ShieldCheck className="w-4 h-4 text-emerald-700" /> Privacy Settings</div>
          <div className="divide-y divide-slate-100 mt-1" data-testid="privacy-visibility-list">{group("privacy", VIS, "Shown", "Hidden")}</div>
          <div className="mt-5 text-sm font-bold text-slate-900 flex items-center gap-2" data-testid="notification-settings-heading"><Bell className="w-4 h-4 text-emerald-700" /> Notification Settings</div>
          <div className="divide-y divide-slate-100 mt-1" data-testid="notification-prefs-list">{group("notifications", NOTIF, "On", "Off")}</div>
        </>
      )}
    </section>
  );
}
